/**
 * PlaybackSpeedDialModal — full-screen circular dial for picking playback
 * speed. Built Jun 4 2026 to match the reference design provided by the
 * user (large circle, drag-the-knob feels, surrounding speed labels,
 * crimson-red sweep for >1× and indigo for <1×).
 *
 * Props:
 *   - open          : boolean — show/hide
 *   - value         : number  — current speed (e.g. 1.0)
 *   - onChange(v)   : commit a new speed (called on Apply)
 *   - onClose()     : close without applying
 *
 * Dial mechanics:
 *   - 11 discrete steps mapped to 0.5 → 3.0 in 0.25 increments.
 *   - The active arc is filled clockwise from 12 o'clock.
 *   - Tap any speed label to snap. Drag the knob to scrub.
 *   - Apply commits the choice + closes.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Gauge, X } from 'lucide-react';

const STEPS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3];
const SIZE = 360;     // dial diameter (label-safe viewBox)
const STROKE = 30;    // sweep ring thickness
const DIAL_R = 124;   // inner dial radius (sweep)
const LABEL_R = 156;  // distance for the orbit labels

// Map step index → polar angle (radians). 12 o'clock = -π/2. Sweep
// clockwise across the lower half of the circle, so 0.5 sits at the
// bottom-left, 3.0 at the bottom-right. Mirrors the reference image.
const ANGLE_FOR = (i) => {
  // -210° to +30° in 24° steps == 10 gaps for 11 stops. Start sweep at
  // top-ish so it reads naturally.
  const total = STEPS.length - 1;
  const startDeg = -210;
  const endDeg = 30;
  const deg = startDeg + ((endDeg - startDeg) * (i / total));
  return (deg * Math.PI) / 180;
};

const polar = (cx, cy, r, angle) => ({
  x: cx + r * Math.cos(angle),
  y: cy + r * Math.sin(angle),
});

// Build an arc path between two angles around (cx, cy, r). sweepFlag=1
// when arc length > π.
const arcPath = (cx, cy, r, startAngle, endAngle) => {
  const start = polar(cx, cy, r, startAngle);
  const end = polar(cx, cy, r, endAngle);
  const delta = endAngle - startAngle;
  const largeArc = Math.abs(delta) > Math.PI ? 1 : 0;
  const sweep = delta >= 0 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`;
};

const PlaybackSpeedDialModal = ({ open, value = 1, onChange, onClose }) => {
  // Snap incoming value to the nearest known step.
  const initialIdx = useMemo(() => {
    let best = 2; let bestDelta = Infinity;
    STEPS.forEach((s, i) => {
      const d = Math.abs(s - value);
      if (d < bestDelta) { bestDelta = d; best = i; }
    });
    return best;
  }, [value]);

  const [idx, setIdx] = useState(initialIdx);
  const dragRef = useRef(false);
  const svgRef = useRef(null);

  useEffect(() => { if (open) setIdx(initialIdx); }, [open, initialIdx]);

  // Close on Escape — keyboard accessibility for desktop users.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const r = DIAL_R;

  const startAngle = ANGLE_FOR(0);
  const currentAngle = ANGLE_FOR(idx);
  const speed = STEPS[idx];
  const isAbove = speed > 1;
  const isBelow = speed < 1;
  const accent = isAbove ? '#dc2626' : isBelow ? '#6366f1' : '#475569';
  const accentSoft = isAbove ? '#7f1d1d' : isBelow ? '#312e81' : '#1e293b';

  // Pointer → angle → nearest step index. The dial is anchored at
  // (cx, cy) inside the SVG viewport; we convert clientX/Y to local
  // coords using getBoundingClientRect.
  const pointToIdx = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return idx;
    const rect = svg.getBoundingClientRect();
    const localX = ((clientX - rect.left) / rect.width) * SIZE;
    const localY = ((clientY - rect.top) / rect.height) * SIZE;
    const angle = Math.atan2(localY - cy, localX - cx);
    let bestI = 0;
    let bestDelta = Infinity;
    for (let i = 0; i < STEPS.length; i++) {
      const a = ANGLE_FOR(i);
      // Normalise both angles to (−π, π] and compute the smallest
      // angular distance accounting for wrap-around.
      let d = Math.atan2(Math.sin(angle - a), Math.cos(angle - a));
      d = Math.abs(d);
      if (d < bestDelta) { bestDelta = d; bestI = i; }
    }
    return bestI;
  };

  const onPointerDown = (e) => {
    dragRef.current = true;
    const nextIdx = pointToIdx(e.clientX, e.clientY);
    setIdx(nextIdx);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setIdx(pointToIdx(e.clientX, e.clientY));
  };
  const onPointerUp = (e) => {
    dragRef.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  const knob = polar(cx, cy, r, currentAngle);

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-black/95 backdrop-blur-xl animate-[fadeIn_120ms_ease-out]"
      data-testid="player-speed-dial-modal"
    >
      <header className="px-5 pt-5 pb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={onClose}
          className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white active:scale-95 transition"
          aria-label="Close playback speed"
          data-testid="speed-dial-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">Playback</p>
          <h2 className="text-base font-bold text-white inline-flex items-center gap-2">
            <Gauge className="w-4 h-4 text-rose-300" /> Playback speed
          </h2>
        </div>
        <div className="w-11" />
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-8 select-none">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width="84vw"
          style={{ maxWidth: 360, touchAction: 'none' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="speed-dial-svg"
        >
          {/* Outer faint ring (background of the dial) */}
          <circle cx={cx} cy={cy} r={r} fill="rgba(15,23,42,0.4)" stroke="rgba(148,163,184,0.08)" strokeWidth="1" />

          {/* The base track (full sweep range, very dim) */}
          <path
            d={arcPath(cx, cy, r, startAngle, ANGLE_FOR(STEPS.length - 1))}
            fill="none"
            stroke="rgba(255,255,255,0.04)"
            strokeWidth={STROKE}
            strokeLinecap="round"
          />

          {/* Active sweep — coloured by the selected step relative to 1× */}
          {idx !== 2 && (
            <path
              d={arcPath(
                cx, cy, r,
                idx < 2 ? currentAngle : ANGLE_FOR(2),
                idx < 2 ? ANGLE_FOR(2) : currentAngle,
              )}
              fill="none"
              stroke={accent}
              strokeWidth={STROKE}
              strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 12px ${accent}aa)` }}
            />
          )}

          {/* Surrounding step labels — every step (every 0.25) shown */}
          {STEPS.map((s, i) => {
            const a = ANGLE_FOR(i);
            const p = polar(cx, cy, LABEL_R, a);
            const isActive = i === idx;
            const label = s === 1 ? '1×' : `${s.toFixed(2).replace(/0$/, '')}×`;
            return (
              <text
                key={s}
                x={p.x}
                y={p.y + 5}
                textAnchor="middle"
                fontSize="13"
                fontWeight="700"
                fill={isActive ? '#ffffff' : 'rgba(255,255,255,0.45)'}
                fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
                style={{ cursor: 'pointer' }}
                onClick={() => setIdx(i)}
              >
                {label}
              </text>
            );
          })}

          {/* Inner dark hub */}
          <circle cx={cx} cy={cy} r={r - STROKE / 2 - 4} fill="#0b1020" stroke={accentSoft} strokeWidth="1" />

          {/* Big number — centered slightly above the central hub */}
          <text
            x={cx}
            y={cy + 6}
            textAnchor="middle"
            fontSize="44"
            fontWeight="900"
            fill="#ffffff"
            fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
            data-testid="speed-dial-current-value"
          >
            {speed.toFixed(2).replace(/0$/, '')}×
          </text>
          <text
            x={cx}
            y={cy + 36}
            textAnchor="middle"
            fontSize="10"
            fontWeight="800"
            fill="rgba(255,255,255,0.5)"
            letterSpacing="2"
            fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
          >
            PLAYBACK SPEED
          </text>

          {/* Draggable knob — the visual cue at the current step */}
          <circle
            cx={knob.x}
            cy={knob.y}
            r={12}
            fill="#ffffff"
            stroke={accent}
            strokeWidth="3"
            style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))' }}
          />
        </svg>

        {/* Inline step pills as a backup affordance — quick taps for the
            ten most useful speeds. Mirrors the dial selection. */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-1.5" data-testid="speed-dial-presets">
          {STEPS.map((s, i) => {
            const isActive = i === idx;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setIdx(i)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-black tabular-nums tracking-tight border transition active:scale-95 ${
                  isActive
                    ? s === 1
                      ? 'bg-slate-700 text-white border-slate-500'
                      : s > 1
                      ? 'bg-rose-600 text-white border-rose-400'
                      : 'bg-indigo-600 text-white border-indigo-400'
                    : 'bg-white/5 text-white/60 border-white/10 hover:border-white/30 hover:text-white'
                }`}
                data-testid={`speed-dial-preset-${String(s).replace('.', '-')}`}
              >
                {s}×
              </button>
            );
          })}
        </div>
      </main>

      <footer className="px-5 pb-5">
        <button
          type="button"
          onClick={() => { onChange?.(STEPS[idx]); onClose?.(); }}
          className="w-full py-4 rounded-full bg-white/10 hover:bg-white/15 text-white text-base font-bold active:scale-[0.99] transition border border-white/15"
          data-testid="speed-dial-apply"
        >
          Apply
        </button>
      </footer>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
};

export default PlaybackSpeedDialModal;
